// FILENAME: frontend/src/components/Layout.jsx
import React, { useMemo, useState } from 'react';
import { AppBar, Box, Toolbar } from '@mui/material';
import { Outlet, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth, GoogleDomainSignInButton } from '../auth/googleDomainAuth.jsx';
import useSoketi from '../hooks/useSoketi';
import { useThemeMode } from '../hooks/useThemeMode.jsx';
import { useSearch } from '../context/SearchContext.jsx';
import { useDashboard } from '../context/DashboardContext.jsx';
import { TAB_BULK_SEND, TAB_ENROLLMENT, TAB_SELECTIONS, TAB_VMT_STAGING } from './dashboard/constants';
import PageTransition from './PageTransition';
import LayoutAppBarMobile from './layout/LayoutAppBarMobile.jsx';
import LayoutSnackbars from './layout/LayoutSnackbars.jsx';
import useRefreshSync from '../hooks/layout/useRefreshSync';
import useSoftRefreshQueue from '../hooks/layout/useSoftRefreshQueue';
import useCacheStatusPolling from '../hooks/layout/useCacheStatusPolling';
import useRealtimeSubscriptions from '../hooks/layout/useRealtimeSubscriptions';
import useSseFallback from '../hooks/layout/useSseFallback';

const Layout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [, setSearchParams] = useSearchParams();
  const { clearSearch } = useSearch();
  const { resetDashboard, currentTab, isDetailOpen, requestTabChange } = useDashboard();
  const { user, logout } = useAuth();
  const { mode, toggleMode } = useThemeMode();
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const queryClient = useQueryClient();
  const { enabled: realtimeEnabled, connected: realtimeConnected, pusher } = useSoketi();
  const serverConnectionLive = realtimeEnabled && realtimeConnected;
  const serverConnectionTooltip = serverConnectionLive
    ? 'Server updates are connected. GM data still refreshes on the normal schedule.'
    : 'Server update notifications may be delayed. GM data still refreshes on the normal schedule.';

  const isSoftRefreshMode = useMemo(() => {
    const path = location.pathname || '';
    const isDailyDigest = path.startsWith('/daily-digest');
    const isDealer360 = path.startsWith('/dealer/');
    const isDashboard = path === '/enrollment-status';
    const isVmtStaging = isDashboard && currentTab === TAB_VMT_STAGING;
    const isBulkSend = isDashboard && currentTab === TAB_BULK_SEND;
    return Boolean(isDetailOpen || isDailyDigest || isDealer360 || isVmtStaging || isBulkSend);
  }, [currentTab, isDetailOpen, location.pathname]);

  const {
    setIsSyncing,
    setProgramSyncing,
    syncSpinning,
    handleForceSync,
    handleAbortSync,
    formatAbortSummary,
    isSyncRequestPending,
    isAbortPending,
  } = useRefreshSync({ setSnackbar });

  const { softRefreshSnackbar, queueProgramRefresh, handleSoftRefreshApply, closeSoftRefreshSnackbar } =
    useSoftRefreshQueue({ isSoftRefreshMode });

  useCacheStatusPolling({ queryClient, realtimeEnabled, realtimeConnected });

  useRealtimeSubscriptions({
    pusher,
    realtimeEnabled,
    realtimeConnected,
    queueProgramRefresh,
    setIsSyncing,
    setProgramSyncing,
    setSnackbar,
    formatAbortSummary,
    queryClient,
  });

  useSseFallback({
    realtimeEnabled,
    realtimeConnected,
    queueProgramRefresh,
    setIsSyncing,
    setProgramSyncing,
    setSnackbar,
    formatAbortSummary,
    queryClient,
  });

  const goHome = () => {
    // Hard reset so stale URL params do not keep forcing navigation/search modes.
    clearSearch?.();
    resetDashboard?.();
    setSearchParams(new URLSearchParams(), { replace: true });
    navigate('/', { replace: true });
  };
  const goBuySells = () => navigate('/buy-sells');
  const goBillingReconciliation = () => navigate('/billing-reconciliation');
  const goAppsheetAccounts = () => navigate('/appsheet/accounts');
  const goAppsheetOrderItems = () => navigate('/appsheet/orderitems');
  const goReports = () => navigate('/reports');
  const goSettings = () => navigate('/settings');
  const goStory = () => navigate('/story');
  const goDashboardTab = (nextTab) => {
    clearSearch?.();
    requestTabChange(nextTab);
    if (location.pathname !== '/enrollment-status') {
      navigate('/enrollment-status', { replace: true });
    }
  };
  const handleCopyUrl = async () => {
    const url = typeof window !== 'undefined' ? window.location.href : '';
    if (!url) return;
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(url);
        setSnackbar({ open: true, message: 'Copied URL to clipboard.', severity: 'success' });
        return;
      }
    } catch (_err) {
      // noop
    }
    setSnackbar({ open: true, message: 'Clipboard access is blocked. Copy the URL from the address bar.', severity: 'warning' });
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <AppBar position="sticky">
        <Toolbar sx={{ gap: 1 }}>
          <LayoutAppBarMobile
            serverConnectionLive={serverConnectionLive}
            serverConnectionTooltip={serverConnectionTooltip}
            mode={mode}
            toggleMode={toggleMode}
            menuAutoOpen={location.pathname === '/'}
            menuAutoOpenToken={location.key}
            handleCopyUrl={handleCopyUrl}
            goStory={goStory}
            goHome={goHome}
            goEnrollmentStatus={() => goDashboardTab(TAB_ENROLLMENT)}
            goSelections={() => goDashboardTab(TAB_SELECTIONS)}
            goVmtStaging={() => goDashboardTab(TAB_VMT_STAGING)}
            goBulkSend={() => goDashboardTab(TAB_BULK_SEND)}
            goBuySells={goBuySells}
            goBillingReconciliation={goBillingReconciliation}
            goAppsheetAccounts={goAppsheetAccounts}
            goAppsheetOrderItems={goAppsheetOrderItems}
            goReports={goReports}
            handleForceSync={handleForceSync}
            handleAbortSync={handleAbortSync}
            syncSpinning={syncSpinning}
            isAbortPending={isAbortPending}
            isSyncRequestPending={isSyncRequestPending}
            goSettings={goSettings}
            user={user}
            logout={logout}
            GoogleDomainSignInButton={GoogleDomainSignInButton}
          />
        </Toolbar>
      </AppBar>
      <Box component="main" sx={{ flexGrow: 1, p: { xs: 1.5, sm: 2.5 } }}>
        <PageTransition transitionKey={location.pathname}>
          <Outlet />
        </PageTransition>
      </Box>
      <LayoutSnackbars
        softRefreshSnackbar={softRefreshSnackbar}
        onSoftClose={closeSoftRefreshSnackbar}
        onSoftApply={handleSoftRefreshApply}
        snackbar={snackbar}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
      />
      <style>
        {`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}
      </style>
    </Box>
  );
};

export default Layout;
